<div class="flex flex-wrap items-center justify-between gap-2 mb-6">
  <div class="flex flex-wrap gap-2">
    @if (canEdit()) {
      <p-button (onClick)="createStage.emit()">
        <i class="fa-solid fa-plus mr-2"></i> {{ 'stages.button.new' | translate }}
      </p-button>
      @if (showAddMeasurement()) {
        <p-button (onClick)="createMeasurement.emit()">
          <i class="fa-solid fa-plus mr-2"></i> {{ 'measurement.button' | translate }}
        </p-button>
      }
    }
    <p-button (onClick)="compare.emit()" [outlined]="true" [loading]="compareLoading()">
      <i class="fa-solid fa-code-compare mr-2"></i> {{ 'stages.compare_measurements' | translate }}
    </p-button>
    <p-button (onClick)="summarize.emit()" [outlined]="true" [loading]="summarizeLoading()">
      <i class="fa-solid fa-chart-bar mr-2"></i> {{ 'stages.summarize_measurements' | translate }}
    </p-button>
  </div>

  <p-selectbutton
    [options]="viewModeOptions"
    [ngModel]="viewMode()"
    (onChange)="setViewMode($event.value)"
    optionValue="value"
    [allowEmpty]="false"
  >
    <ng-template #item let-item>
      <i [class]="item.icon"></i>
    </ng-template>
  </p-selectbutton>
</div>

<!-- SELECTION SUMMARY (informative only) -->
@if (hasSelection()) {
  <p-message severity="secondary" styleClass="w-full mb-4">
    <span class="flex items-center gap-2">
      <i class="fa-solid fa-check-double"></i>
      <span>
        @if (selectedStageCount() > 0 && selectedMeasurementCount() > 0) {
          {{ 'stages.selection.stagesAndMeasurements' | translate:{ stages: selectedStageCount(), measurements: selectedMeasurementCount() } }}
        } @else if (selectedStageCount() > 0) {
          {{ 'stages.selection.stages' | translate:{ count: selectedStageCount() } }}
        } @else {
          {{ 'stages.selection.measurements' | translate:{ count: selectedMeasurementCount() } }}
        }
      </span>
      <button
        type="button"
        class="ml-1 font-semibold underline underline-offset-2 hover:no-underline cursor-pointer"
        (click)="clearSelection()"
      >
        {{ 'stages.selection.clear' | translate }}
      </button>
    </span>
  </p-message>
}

<!-- LOADING / ERROR / EMPTY -->
@if (stagesLoading() || measurementsLoading()) {
  <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
    <p-skeleton height="6rem" borderRadius="0.5rem"></p-skeleton>
    <p-skeleton height="6rem" borderRadius="0.5rem"></p-skeleton>
    <p-skeleton height="6rem" borderRadius="0.5rem"></p-skeleton>
  </div>
} @else {
  @if (stagesError() && measurementsError()) {
    <p-message severity="error" styleClass="w-full mb-4">
      <i class="fa-solid fa-triangle-exclamation mr-2"></i>
      {{ 'stages.error.stagesAndMeasurements' | translate }}
    </p-message>
  } @else if (stagesError()) {
    <p-message severity="error" styleClass="w-full mb-4">
      <i class="fa-solid fa-triangle-exclamation mr-2"></i>
      {{ 'stages.error.stages' | translate }}
    </p-message>
  } @else if (measurementsError()) {
    <p-message severity="error" styleClass="w-full mb-4">
      <i class="fa-solid fa-triangle-exclamation mr-2"></i>
      {{ 'stages.error.measurements' | translate }}
    </p-message>
  }

  @if (!stagesError() && !measurementsError() && stages().length === 0 && measurements().length === 0) {
    <p-message severity="info" styleClass="w-full">
      <i class="fa-solid fa-layer-group mr-2"></i>
      {{ (showAddMeasurement() ? 'stages.not-found-any' : 'stages.not-found') | translate }}
    </p-message>
  }
}

@if (!stagesLoading() && !measurementsLoading() && (stages().length > 0 || measurements().length > 0)) {
  @if (viewMode() === 'grid') {
    <!-- GRID: folder cards, then measurement cards -->
    @if (stages().length > 0) {
      <div class="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
        @for (stage of stages(); track stage._id) {
          <cosmos-folder-card
            [stage]="stage"
            layout="grid"
            [canEdit]="canEdit()"
            [selected]="isSelected(stage._id)"
            (open)="emitOpenStage(stage._id)"
            (select)="toggleSelected(stage._id)"
            (rename)="emitRenameStage(stage._id)"
            (move)="emitMoveStage(stage._id)"
          />
        }
      </div>
    }
    @if (measurements().length > 0) {
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        @for (measurement of measurements(); track measurement._id) {
          <cosmos-measurement-card
            [measurement]="measurement"
            [selectable]="true"
            [selected]="isSelected(measurement._id)"
            (open)="emitOpenMeasurement(measurement._id)"
            (select)="toggleSelected(measurement._id)"
          />
        }
      </div>
    }
  } @else {
    <!-- LIST: one concatenated list inside a card — folder rows first, a
         thicker rule, then measurement rows. -->
    <p-card class="stage-browser-list block">
      <div class="stage-browser-list__rows flex flex-col">
        @for (stage of stages(); track stage._id; let first = $first; let last = $last) {
          <cosmos-folder-card
            [stage]="stage"
            layout="list"
            [isLast]="last"
            [isFirst]="first"
            [isAbsoluteLast]="last && measurements().length === 0"
            [canEdit]="canEdit()"
            [selected]="isSelected(stage._id)"
            (open)="emitOpenStage(stage._id)"
            (select)="toggleSelected(stage._id)"
            (rename)="emitRenameStage(stage._id)"
            (move)="emitMoveStage(stage._id)"
          />
        }

        @if (stages().length > 0 && measurements().length > 0) {
          <div class="stage-browser-list__boundary"></div>
        }

        @for (measurement of measurements(); track measurement._id; let first = $first; let last = $last) {
          <cosmos-measurement-row
            [measurement]="measurement"
            [isLast]="last"
            [isFirst]="first && stages().length === 0"
            [isAbsoluteLast]="last"
            [selected]="isSelected(measurement._id)"
            (open)="emitOpenMeasurement(measurement._id)"
            (select)="toggleSelected(measurement._id)"
          />
        }
      </div>
    </p-card>
  }
}
